import { useState, useEffect } from "react";
import { Heart, MessageCircle, Send, Users, Dumbbell, Apple, Plus, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Navbar } from "../components/Navbar";
import { ImageUpload } from "../components/ImageUpload";
import api from "../lib/api";

export default function Community() {
  const [posts, setPosts] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [newPost, setNewPost] = useState({ content: "", community_type: "fitness", image_url: "" });
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem("vita_user");
    if (stored) setUser(JSON.parse(stored));
  }, []);

  useEffect(() => {
    fetchPosts(activeTab);
  }, [activeTab]);

  const fetchPosts = async (type) => {
    setLoading(true);
    try {
      const res = await api.get(`/community/posts?community_type=${type}`);
      setPosts(res.data || []);
    } catch (err) { console.error('Error:', err.message || err); }
    setLoading(false);
  };

  const handlePost = async () => {
    if (!newPost.content.trim()) return;
    try {
      await api.post("/community/posts", {
        content: newPost.content,
        community_type: newPost.community_type,
        image_url: newPost.image_url || null,
      });
      setShowCreate(false);
      setNewPost({ content: "", community_type: "fitness", image_url: "" });
      fetchPosts(activeTab);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to post. You may need to upgrade to Premium.");
    }
  };

  const handleLike = async (postId) => {
    if (!user) return;
    try {
      const res = await api.post(`/community/posts/${postId}/like`);
      setPosts((prev) =>
        prev.map((p) =>
          p.post_id === postId
            ? { ...p, likes: res.data.liked ? [...(p.likes || []), user.user_id] : (p.likes || []).filter((id) => id !== user.user_id) }
            : p
        )
      );
    } catch (err) { console.error('Error:', err.message || err); }
  };

  const handleComment = async (postId, content) => {
    if (!content.trim()) return;
    try {
      await api.post(`/community/posts/${postId}/comment`, { content });
      fetchPosts(activeTab);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to comment");
    }
  };

  return (
    <div className="min-h-screen bg-transparent" data-testid="community-page">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-[#0F172A]">Community</h1>
            <p className="text-slate-500 mt-1">Connect, share, and grow together</p>
          </div>
          {user && (
            <Button
              onClick={() => setShowCreate(true)}
              data-testid="create-post-btn"
              className="bg-[#0B6E4F] hover:bg-[#095A40] text-white rounded-full px-6 font-semibold shadow-lg hover:shadow-xl transition-all"
            >
              <Plus className="mr-2 h-4 w-4" /> New Post
            </Button>
          )}
        </div>

        {/* Create Post Modal */}
        {showCreate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="create-post-modal">
            <div className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-['Outfit'] text-lg font-semibold">Create Post</h3>
                <button onClick={() => setShowCreate(false)} className="p-1 hover:bg-slate-100 rounded-full">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex gap-2 mb-4">
                {["fitness", "health"].map((type) => (
                  <button
                    key={type}
                    onClick={() => setNewPost({ ...newPost, community_type: type })}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      newPost.community_type === type
                        ? "bg-[#0B6E4F] text-white"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    {type === "fitness" ? "Fitness" : "Health"}
                  </button>
                ))}
              </div>
              <textarea
                data-testid="post-content-input"
                placeholder="Share your journey, tips, or motivation..."
                value={newPost.content}
                onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                className="w-full h-32 bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm resize-none focus:ring-2 focus:ring-[#0B6E4F] focus:border-transparent outline-none"
              />
              <ImageUpload
                onUpload={(url) => setNewPost({ ...newPost, image_url: url })}
                currentUrl={newPost.image_url}
                className="mt-3"
              />
              <div className="flex justify-end gap-3 mt-4">
                <Button variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
                <Button
                  onClick={handlePost}
                  data-testid="submit-post-btn"
                  className="bg-[#0B6E4F] hover:bg-[#095A40] text-white rounded-full px-6"
                >
                  Post
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="all" onValueChange={setActiveTab}>
          <TabsList className="bg-white rounded-xl p-1 shadow-sm border border-slate-100 mb-6">
            <TabsTrigger value="all" className="rounded-lg data-[state=active]:bg-[#F2F9F6] data-[state=active]:text-[#0B6E4F] font-medium">
              <Users className="mr-2 h-4 w-4" /> All
            </TabsTrigger>
            <TabsTrigger value="fitness" className="rounded-lg data-[state=active]:bg-[#F2F9F6] data-[state=active]:text-[#0B6E4F] font-medium">
              <Dumbbell className="mr-2 h-4 w-4" /> Fitness
            </TabsTrigger>
            <TabsTrigger value="health" className="rounded-lg data-[state=active]:bg-[#F2F9F6] data-[state=active]:text-[#0B6E4F] font-medium">
              <Apple className="mr-2 h-4 w-4" /> Health
            </TabsTrigger>
          </TabsList>

          {["all", "fitness", "health"].map((tab) => (
            <TabsContent key={tab} value={tab}>
              {loading ? (
                <div className="text-center py-16">
                  <div className="h-10 w-10 border-3 border-[#0B6E4F] border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              ) : posts.length === 0 ? (
                <div className="bg-white border border-slate-100 rounded-2xl p-16 shadow-sm text-center">
                  <Users className="h-12 w-12 text-slate-200 mx-auto mb-4" />
                  <p className="text-slate-500 mb-2">No posts yet in this community</p>
                  <p className="text-sm text-slate-400">Be the first to share something!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {posts.map((post) => (
                    <PostCard key={post.post_id} post={post} user={user} onLike={handleLike} onComment={handleComment} />
                  ))}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
}

const PostCard = ({ post, user, onLike, onComment }) => {
  const [commentText, setCommentText] = useState("");
  const [showComments, setShowComments] = useState(false);
  const isLiked = user && post.likes?.includes(user.user_id);

  return (
    <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm" data-testid={`post-${post.post_id}`}>
      {/* Author */}
      <div className="flex items-center gap-3 mb-4">
        {post.author?.picture ? (
          <img src={post.author.picture} alt="" className="h-10 w-10 rounded-full object-cover" />
        ) : (
          <div className="h-10 w-10 rounded-full bg-[#0B6E4F] flex items-center justify-center text-white font-semibold text-sm">
            {post.author?.name?.[0]?.toUpperCase() || "U"}
          </div>
        )}
        <div>
          <p className="font-medium text-sm text-[#0F172A]">{post.author?.name || "Anonymous"}</p>
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              post.community_type === "fitness" ? "bg-blue-50 text-blue-600" : "bg-green-50 text-green-600"
            }`}>
              {post.community_type}
            </span>
            <span className="text-xs text-slate-400">{new Date(post.created_at).toLocaleDateString()}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <p className="text-sm text-slate-700 leading-relaxed mb-4 whitespace-pre-wrap">{post.content}</p>

      {/* Post Image */}
      {post.image_url && (
        <div className="mb-4 rounded-xl overflow-hidden border border-slate-100">
          <img src={post.image_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${post.image_url}` : post.image_url} alt="" className="w-full max-h-96 object-cover" />
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-6 pt-3 border-t border-slate-50">
        <button
          onClick={() => onLike(post.post_id)}
          className={`flex items-center gap-2 text-sm transition-colors ${isLiked ? "text-red-500" : "text-slate-400 hover:text-red-500"}`}
          data-testid={`like-btn-${post.post_id}`}
        >
          <Heart className={`h-4 w-4 ${isLiked ? "fill-current" : ""}`} />
          {post.likes?.length || 0}
        </button>
        <button
          onClick={() => setShowComments(!showComments)}
          className="flex items-center gap-2 text-sm text-slate-400 hover:text-[#0B6E4F] transition-colors"
          data-testid={`comment-toggle-${post.post_id}`}
        >
          <MessageCircle className="h-4 w-4" />
          {post.comments?.length || 0}
        </button>
      </div>

      {/* Comments */}
      {showComments && (
        <div className="mt-4 pt-4 border-t border-slate-50">
          {post.comments?.map((c, i) => (
            <div key={i} className="flex items-start gap-3 mb-3">
              <div className="h-7 w-7 rounded-full bg-slate-200 flex items-center justify-center text-xs font-semibold text-slate-600 flex-shrink-0">
                {c.author_name?.[0]?.toUpperCase() || "U"}
              </div>
              <div className="bg-slate-50 rounded-xl px-3 py-2 flex-1">
                <p className="text-xs font-medium text-slate-700">{c.author_name}</p>
                <p className="text-xs text-slate-600 mt-0.5">{c.content}</p>
              </div>
            </div>
          ))}
          {user && (
            <div className="flex items-center gap-2 mt-2">
              <input
                type="text"
                placeholder="Write a comment..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    onComment(post.post_id, commentText);
                    setCommentText("");
                  }
                }}
                className="flex-1 h-9 bg-slate-50 border border-slate-200 rounded-full px-4 text-sm focus:ring-1 focus:ring-[#0B6E4F] outline-none"
                data-testid={`comment-input-${post.post_id}`}
              />
              <button
                onClick={() => { onComment(post.post_id, commentText); setCommentText(""); }}
                className="p-2 rounded-full bg-[#0B6E4F] text-white hover:bg-[#095A40] transition-colors"
              >
                <Send className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
